/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React from "react";

import { useRouter } from "next/router";
import { isElectron } from "react-device-detect";
import styled from "styled-components";

import { Layer } from "@vienna/common";

import { DEVICE } from "src/app/globalStyles";
import { DrilledBlock } from "src/components/DrilledView/DrilledBlock";
import { DrilledPlate } from "src/components/DrilledView/DrilledPlate";
import { HOTKEYS } from "src/components/Hotkeys/hotkeys";
import { XButtonSVG } from "src/components/UI/Buttons";
import { useHotkey } from "src/lib/hooks/useHotkey";

interface Params {
  blockId?: string;
  pageId?: string;
}
export function DrilledView() {
  const router = useRouter();
  const { blockId, pageId }: Params = router.query as any;

  // strip the blockId/pageId query param and return back to whatever the underlying
  // page is
  const handleClose = () => {
    if (router.asPath.includes(`?blockId=${blockId}`)) {
      router.replace(router.asPath.replace(`?blockId=${blockId}`, ""));
    } else if (router.asPath.includes(`?pageId=${pageId}`)) {
      router.replace(router.asPath.replace(`?pageId=${pageId}`, ""));
    }
  };

  useHotkey(HOTKEYS.ESCAPE, handleClose);

  return (
    <BackgroundOverlay onClick={handleClose} id="cy-drilled-background-overlay">
      <Wrapper>
        <CloseWrapper
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            handleClose();
          }}
        >
          <XButtonSVG />
        </CloseWrapper>
        {blockId && <DrilledBlock blockId={blockId} />}
        {pageId && <DrilledPlate plateId={pageId} />}
      </Wrapper>
    </BackgroundOverlay>
  );
}

const BackgroundOverlay = styled.div`
  line-height: 1.15;
  font-family: "Inter";
  background: rgba(131, 131, 131, 0.304);
  // add a blur to the background
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  overflow: scroll;
  position: fixed;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  z-index: ${Layer.OVERLAY};
  padding: 20px;
  @media (max-width: ${DEVICE.small}) {
    padding: 0;
  }
`;

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 20px;
  @media (max-width: ${DEVICE.small}) {
    flex-direction: column;
  }
`;

const CloseWrapper = styled.div`
  position: absolute;
  top: ${isElectron ? "45px" : "20px"};
  left: 15px;
  width: 30px;
  height: 30px;
  color: ${({ theme }) => theme.primary};
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  z-index: 999;
  &:hover {
    /* background-color: rgba(0, 0, 0, 0.3); */
    color: ${({ theme }) => theme.background};
  }
`;
